// The single place this app reloads itself.
//
// Two independent *automatic* reload paths existed before this module: the
// vite-pwa auto mode `activated` listener and the router's `defaultOnCatch`
// chunk-error handler. A safety policy with two bypasses is not a policy, so
// both now route through this arbiter — the only place the app reloads itself
// without the user asking. (The explicit "try again" buttons in `ErrorPage` and
// friends still call `reload()` directly; a reload the user pressed for is not
// something to arbitrate.)

import { getFormDirtiness } from "./dirtyFormRegistry";
import { awaitPendingWorkSettled } from "./pendingWorkSnapshot";
import { decideUpdateApply } from "./updateApplyPolicy";

export type ReloadReason = "sw-activated" | "chunk-missing";

// A reload is terminal for this document, so the guard is deliberately *not*
// released after one is issued: a second request that arrives while the browser
// is tearing the page down must be a no-op, not a second navigation.
let inFlight = false;

// The reason the in-flight request will act on once its settle completes, which
// is not necessarily the reason it started with. `chunk-missing` outranks
// `sw-activated`, so a chunk error arriving mid-settle *upgrades* the in-flight
// request instead of being dropped by the guard above. Dropping it would be
// unrecoverable: `main.tsx` writes its 10 s chunk-reload guard before calling,
// so the next chunk error inside that window is suppressed too and the user is
// left on a broken screen — exactly what §6.1 says this path must prevent. The
// reverse (a `sw-activated` arriving while a `chunk-missing` settles) stays
// dropped; the stronger reason already reloads unconditionally.
let inFlightReason: ReloadReason | null = null;

// The escalation clock the final re-check reads. `serviceWorkerClient` is the
// single writer — it owns `deferredUpdateStorage` and the service-worker
// revision the deadline is keyed to. Pushing the value down instead of pulling
// it keeps the dependency direction acyclic (serviceWorkerClient → appReload):
// without it the arbiter would deny an apply the client already granted under
// escalation, and `APPLY_ESCALATION_MS` would bound nothing.
let deferredSinceMs: number | null = null;

export function setDeferredSince(atMs: number | null): void {
	deferredSinceMs = atMs;
}

/**
 * Reload the app for `reason`, if and only if doing so destroys no user work.
 *
 * Both reasons await `awaitPendingWorkSettled()` first: `lib/offlineQueue`
 * writes are async read-modify-write cycles against IndexedDB, so a
 * synchronous count read cannot tell a quiescent queue from one mid-write.
 * Waiting for the settle is what makes "nothing committed is lost" true.
 *
 * The reasons then diverge (§6.1 of the design):
 * - `sw-activated` — nothing is broken, so the policy decides and a dirty form
 *   defers the reload indefinitely.
 * - `chunk-missing` — the route already failed to load and the screen is
 *   broken. Deferring leaves the user staring at an error, so this path reloads
 *   regardless of form dirtiness; queued work is still flushed first.
 *
 * Concurrent requests still produce exactly one reload, but a `chunk-missing`
 * that arrives while a `sw-activated` is settling upgrades that request rather
 * than being dropped — see `inFlightReason`.
 */
export async function requestAppReload(reason: ReloadReason): Promise<void> {
	if (inFlight) {
		if (reason === "chunk-missing") inFlightReason = "chunk-missing";
		return;
	}
	inFlight = true;
	inFlightReason = reason;

	const pendingWork = await awaitPendingWorkSettled();

	// Read the reason *after* the settle, not before: a `chunk-missing` that
	// arrived while this request was awaiting has upgraded it, and that upgrade
	// is what must decide, otherwise the chunk error is silently swallowed.
	if (inFlightReason === "sw-activated") {
		// Everything below this line up to `reload()` is synchronous on purpose.
		// An `await` here would reopen the window this settle just closed.
		const decision = decideUpdateApply({
			pendingWork,
			formDirtiness: getFormDirtiness(),
			deferredSinceMs,
			now: Date.now(),
		});
		if (decision !== "apply-now") {
			inFlight = false;
			inFlightReason = null;
			return;
		}
	}

	window.location.reload();
}

// Exposed for unit tests only — allows resetting module state between test cases.
export function _resetForTesting() {
	inFlight = false;
	inFlightReason = null;
	deferredSinceMs = null;
}
